/**
 * Client-Side Image Compressor for Construction Foremen
 * Compresses camera photos (8-15 MB) into lightweight WebP/JPEG (~150-250 KB)
 * and adds an optional timestamp & zone watermark directly onto the canvas.
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  watermarkText?: string;
}

export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<{ dataUrl: string; blob: Blob; originalSize: number; compressedSize: number }> {
  const {
    maxWidth = 1600,
    maxHeight = 1200,
    quality = 0.75,
    watermarkText = `ЖК «Северная Звезда» • ${new Date().toLocaleString('ru-RU')}`,
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Calculate aspect ratio scaling
        if (width > maxWidth || height > maxHeight) {
          if (width / height > maxWidth / maxHeight) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Canvas 2D context not available'));
          return;
        }

        // Draw image
        ctx.drawImage(img, 0, 0, width, height);

        // Draw Watermark Overlay (Footer Bar)
        if (watermarkText) {
          const barHeight = Math.max(32, Math.round(height * 0.045));
          ctx.fillStyle = 'rgba(15, 23, 42, 0.75)'; // Slate 900 semi-transparent
          ctx.fillRect(0, height - barHeight, width, barHeight);

          ctx.fillStyle = '#f8fafc'; // White/Slate 50
          ctx.font = `600 ${Math.max(12, Math.round(barHeight * 0.45))}px sans-serif`;
          ctx.textBaseline = 'middle';
          ctx.fillText(`⚡ ${watermarkText}`, 14, height - barHeight / 2);
        }

        // Export as WebP or JPEG
        const dataUrl = canvas.toDataURL('image/webp', quality);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create compressed blob'));
              return;
            }
            resolve({
              dataUrl,
              blob,
              originalSize: file.size,
              compressedSize: blob.size,
            });
          },
          'image/webp',
          quality
        );
      };
      img.onerror = () => reject(new Error('Failed to load image for compression'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}
